// Turns race events into the original kissaderby.fi/modern cues
// (.taiga/knowledge/landscape/kissaderby-modern-sound.md, "Cue map"):
// post time fanfare; name + foundham, purring while eating; name + atecatnip, trip music once per
// trip; ham sprint for the cat in 2nd; lead change; place calls; cat metal and the doping test at
// the results; a meow when a cat moves fast. On top of the original: a quiet heartbeat when two
// cats fight for the line (heartbeat.wav, made by scripts/heartbeat.mjs).
import { duel } from '../director/drama';
import type { CatId, RaceEvent, RaceSnapshot } from '../race/types';

/** What the director asks of the player. ClipPlayer implements it; tests fake it. */
export interface CuePlayer {
  announce(cat: CatId, event: string, minor?: boolean): void;
  nameOnly(cat: CatId): void;
  sfx(file: string, ttl: number): void;
  after(ms: number, fn: () => void): void;
  running: boolean;
}

const PLACE_CLIP = ['winner.mp3', '2ndplace.mp3', null, '4thplace.mp3', '5thplace.mp3'];
/** The feed's frame period; the original compared positions frame to frame. */
const FEED_MS = 100;
const FAST_MOVE = 0.009;
const HAM_SPRINT_EVERY_MS = 45000;
/** The heartbeat starts this close to the line, about five seconds out, and plays once per duel. */
const HEARTBEAT_FROM = 0.9;
const HEARTBEAT_TTL_S = 30;

export class AudioDirector {
  private lastPos = new Map<CatId, number>();
  private lastSampleT = -Infinity;
  private lastHamSprint = -Infinity;

  constructor(private out: CuePlayer, private rand: () => number = Math.random) {}

  /** nowMs: the player's clock, for the ham sprint throttle. */
  update(snapshot: RaceSnapshot | null, events: RaceEvent[], nowMs: number): void {
    for (const e of events) this.on(e, snapshot, nowMs);
    if (!snapshot) return;
    this.out.running = snapshot.phase === 'running';
    if (snapshot.phase !== 'running') { this.lastPos.clear(); return; }
    for (const c of snapshot.cats) {
      if (c.eating) this.out.sfx('purring.mp3', 4);
      if (c.tripping) this.out.sfx('trip.mp3', 20);
    }
    const d = duel(snapshot);
    if (d && d.leader.displayPos >= HEARTBEAT_FROM) this.out.sfx('heartbeat.wav', HEARTBEAT_TTL_S);
    const elapsed = snapshot.t - this.lastSampleT;
    if (elapsed >= FEED_MS) {
      this.lastSampleT = snapshot.t;
      // Scale to one feed frame, so slow rendering (long gaps between snapshots) does not read as speed.
      const perFrame = FEED_MS / elapsed;
      for (const c of snapshot.cats) {
        const before = this.lastPos.get(c.id);
        if (before !== undefined && before > 0 && (c.signalPos - before) * perFrame > FAST_MOVE && !c.finished) {
          this.out.sfx(this.rand() < 0.5 ? 'meow0.mp3' : 'meow1.mp3', 3);
        }
        this.lastPos.set(c.id, c.signalPos);
      }
    }
  }

  private on(e: RaceEvent, snap: RaceSnapshot | null, nowMs: number): void {
    switch (e.type) {
      case 'phase':
        if (e.phase === 'starting') this.out.sfx('posttime.mp3', 20);
        break;
      case 'ham-start':
        this.out.announce(e.cat, 'foundham.mp3');
        break;
      case 'catnip-catch':
        this.out.announce(e.cat, 'atecatnip.mp3');
        break;
      case 'ham-moved': {
        // As in the original: only for the cat lying 2nd, with the ham ahead of it in its lane, rarely.
        const second = snap?.cats.find((c) => c.place === 2 && !c.finished);
        if (second && second.lane === e.lane && e.pos > second.signalPos + 0.02 && nowMs - this.lastHamSprint > HAM_SPRINT_EVERY_MS) {
          this.lastHamSprint = nowMs;
          this.out.announce(second.id, 'hamsprint.mp3', true);
        }
        break;
      }
      case 'lead-change':
        this.out.announce(e.leader, this.rand() < 0.5 ? '1stplaceovertake1.mp3' : '1stplaceovertake2.mp3');
        break;
      case 'finish': {
        const last = snap?.cats.length ?? 6;
        const clip = e.place >= last ? (this.rand() < 0.1 ? 'lastplacespecial.mp3' : 'lastplace.mp3') : PLACE_CLIP[e.place - 1];
        if (clip) this.out.announce(e.cat, clip);
        else if (e.place === 3) this.out.nameOnly(e.cat);
        break;
      }
      case 'results': {
        this.out.after(1500, () => this.out.sfx('catmetal.mp3', 60));
        const dsq = [...e.results].sort((a, b) => a.place - b.place).find((r) => r.dsq);
        if (dsq) this.out.after(2500, () => this.out.announce(dsq.id, 'dopingtest.mp3'));
        break;
      }
      default:
        break;
    }
  }
}
